import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MonacoEditor from '@monaco-editor/react';
import { AdminFormSection, ACCENT, adminFieldSx, useAdminPageTheme } from '../../../pages/admin/adminUi';
import { STUB_HELPER_TEMPLATES } from './problemFormConstants';

const LANGUAGES = ['python', 'javascript', 'java', 'cpp', 'csharp'];

/**
 * Admin controls for stub hints, helper classes, and custom preamble code.
 */
const ProblemFormStubHelpers = ({
  stubHint,
  onStubHintChange,
  starterPreamble,
  onStarterPreambleChange,
  starterOptions,
  onStarterOptionsChange,
  signature,
  onSignatureChange,
  onRegenerateStarters,
}) => {
  const { border, muted, isDark } = useAdminPageTheme();
  const [activeLang, setActiveLang] = useState('python');

  const insertTemplate = (structName) => {
    const template = STUB_HELPER_TEMPLATES[structName]?.[activeLang];
    if (!template) return;
    const current = starterPreamble?.[activeLang] || '';
    onStarterPreambleChange({
      ...starterPreamble,
      [activeLang]: current ? `${current.trimEnd()}\n\n${template}` : template,
    });
  };

  return (
    <AdminFormSection
      title="Stub helpers & notes"
      description="Show ListNode/TreeNode classes, custom helpers, and hints in the code students see."
    >
      <TextField
        label="Editor hint (shown above code)"
        fullWidth
        multiline
        minRows={2}
        placeholder="The linked list is 0-indexed. Do not modify ListNode class definition."
        value={stubHint || ''}
        onChange={(e) => onStubHintChange(e.target.value)}
        sx={{ ...adminFieldSx, mb: 2 }}
      />

      <TextField
        label="Method notes (embedded in stub as doc comment)"
        fullWidth
        multiline
        minRows={2}
        placeholder="Follow-up: Can you do it in O(1) memory?"
        value={signature?.methodNotes || ''}
        onChange={(e) => onSignatureChange({ ...signature, methodNotes: e.target.value })}
        sx={{ ...adminFieldSx, mb: 2 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={starterOptions?.includeBuiltinStructs !== false}
            onChange={(e) =>
              onStarterOptionsChange({
                ...starterOptions,
                includeBuiltinStructs: e.target.checked,
              })
            }
          />
        }
        label="Auto-include ListNode / TreeNode definitions when signature uses them"
      />

      <Typography variant="caption" sx={{ color: muted, display: 'block', my: 1.5 }}>
        Optional extra code (helper classes, comments) prepended before the Solution class.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {Object.keys(STUB_HELPER_TEMPLATES).map((name) => (
          <Button
            key={name}
            size="small"
            variant="outlined"
            onClick={() => insertTemplate(name)}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 99 }}
          >
            + {name}
          </Button>
        ))}
        {onRegenerateStarters && (
          <Button
            size="small"
            variant="contained"
            onClick={onRegenerateStarters}
            sx={{
              ml: 'auto',
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#0f5c5e' },
            }}
          >
            Regenerate stubs from signature
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
        {LANGUAGES.map((lang) => (
          <Button
            key={lang}
            size="small"
            variant={activeLang === lang ? 'contained' : 'outlined'}
            onClick={() => setActiveLang(lang)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              ...(activeLang === lang ? { bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } } : {}),
            }}
          >
            {lang}
          </Button>
        ))}
      </Box>

      <Accordion
        disableGutters
        elevation={0}
        defaultExpanded={Boolean(starterPreamble?.[activeLang])}
        sx={{
          border: `1px solid ${border}`,
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
            Custom preamble — {activeLang.toUpperCase()}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ height: 200, borderTop: `1px solid ${border}` }}>
            <MonacoEditor
              height="200px"
              language={activeLang === 'cpp' ? 'cpp' : activeLang === 'csharp' ? 'csharp' : activeLang}
              theme={isDark ? 'vs-dark' : 'light'}
              value={starterPreamble?.[activeLang] || ''}
              onChange={(v) =>
                onStarterPreambleChange({ ...starterPreamble, [activeLang]: v || '' })
              }
              options={{ minimap: { enabled: false }, fontSize: 13, wordWrap: 'on' }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </AdminFormSection>
  );
};

export default ProblemFormStubHelpers;
